"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, BellOff, Menu, Search } from "lucide-react";

import { PortalSwitcher } from "@/components/PortalSwitcher";
import type { Portal } from "@/lib/portals";

function useSearchShortcut(inputRef: React.RefObject<HTMLInputElement | null>) {
  const [isMac, setIsMac] = useState(true);

  useEffect(() => {
    setIsMac(navigator.platform.toUpperCase().includes("MAC") || navigator.userAgent.includes("Mac"));
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const key = e.key?.toLowerCase();
      if (key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [inputRef]);

  return isMac ? "⌘K" : "Ctrl+K";
}

function NotificationsPopover({
  onClose,
  anchorRect,
}: {
  onClose: () => void;
  anchorRect: DOMRect;
}) {
  const top = anchorRect.bottom + 8;
  const right = window.innerWidth - anchorRect.right;

  return createPortal(
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1000 }} />
      <div
        className="border border-border bg-popover"
        style={{
          position: "fixed",
          top,
          right,
          width: 320,
          borderRadius: 12,
          boxShadow: "0 4px 10px rgba(46,30,22,0.06), 0 18px 44px rgba(46,30,22,0.12)",
          zIndex: 1001,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          className="border-b border-border"
          style={{
            padding: "14px 16px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span className="text-popover-foreground" style={{ fontSize: 13, fontWeight: 600 }}>Notifications</span>
          <span
            className="bg-muted text-muted-foreground"
            style={{
              fontSize: 11,
              fontWeight: 500,
              borderRadius: 999,
              padding: "2px 8px",
            }}
          >
            0 new
          </span>
        </div>

        {/* Empty state */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 24px",
            gap: 10,
          }}
        >
          <div
            className="bg-muted"
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 2,
            }}
          >
            <BellOff size={18} className="text-muted-foreground" />
          </div>
          <div className="text-popover-foreground" style={{ fontSize: 13, fontWeight: 600 }}>All caught up</div>
          <div className="text-muted-foreground" style={{ fontSize: 12, textAlign: "center", lineHeight: 1.5 }}>
            No notifications right now. We&rsquo;ll let you know when something needs your attention.
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

export default function Topbar({
  currentPortal = "staff",
  mobileNavOpen = false,
  onMobileNavToggle,
}: {
  currentPortal?: Portal;
  mobileNavOpen?: boolean;
  onMobileNavToggle?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const shortcut = useSearchShortcut(inputRef);
  const [notifOpen, setNotifOpen] = useState(false);
  const [bellRect, setBellRect] = useState<DOMRect | null>(null);

  return (
    <div
      className="flex h-[58px] flex-shrink-0 items-center gap-3 border-b border-sidebar-border bg-canvas/80 px-3 sm:gap-4 sm:px-7"
      style={{
        backdropFilter: "saturate(1.4) blur(8px)",
        WebkitBackdropFilter: "saturate(1.4) blur(8px)",
      }}
    >
      <button
        type="button"
        onClick={onMobileNavToggle}
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[9px] text-muted-foreground transition-colors hover:bg-sidebar-accent md:hidden"
        aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"}
        aria-expanded={mobileNavOpen}
      >
        <Menu size={17} />
      </button>

      <PortalSwitcher current={currentPortal} className="shrink-0" />

      {/* Search */}
      <div className="relative flex-1" style={{ maxWidth: 380 }}>
        <Search
          size={13}
          className="absolute left-[10px] top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          ref={inputRef}
          placeholder="Search…"
          className="h-[34px] w-full rounded-[9px] border border-input bg-card pl-8 pr-3 text-[12.5px] text-foreground outline-none transition-[border-color,box-shadow] focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/15"
          style={{
            fontFamily: "inherit",
            paddingRight: 52,
          }}
          onKeyDown={(e) => { if (e.key === "Escape") e.currentTarget.blur(); }}
        />
        <kbd
          className="absolute right-[10px] top-1/2 hidden -translate-y-1/2 rounded border border-input bg-card px-1.5 py-px text-[10px] text-muted-foreground sm:block"
          style={{
            fontFamily: "ui-monospace, monospace",
            boxShadow: "0 1px 2px rgba(46,30,22,0.05)",
            whiteSpace: "nowrap",
          }}
        >
          {shortcut}
        </kbd>
      </div>

      {/* Actions */}
      <div className="ml-auto flex items-center gap-2">
        <button
          ref={bellRef}
          onClick={() => {
            const rect = bellRef.current?.getBoundingClientRect() ?? null;
            setBellRect(rect);
            setNotifOpen((o) => !o);
          }}
          className={`flex h-8 w-8 items-center justify-center rounded-[9px] text-muted-foreground transition-colors hover:bg-sidebar-accent ${
            notifOpen ? "bg-sidebar-accent" : "bg-transparent"
          }`}
          style={{
            border: "none",
            cursor: "pointer",
          }}
        >
          <Bell size={15} />
        </button>
        {notifOpen && bellRect && (
          <NotificationsPopover onClose={() => setNotifOpen(false)} anchorRect={bellRect} />
        )}
      </div>
    </div>
  );
}
